import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import type { OrderStatus } from '@/types/enums'
import { OrderStatus as OS } from '@/types/enums'
import { statusLabelRu } from '@/utils/status-transitions'
import { Button } from '@/components/ui/Button'
import { SearchInput } from '@/components/ui/SearchInput'

const ALL_STATUSES: OrderStatus[] = [
  OS.DRAFT,
  OS.SEARCH,
  OS.DRIVING,
  OS.ARRIVED,
  OS.INSPECTION,
  OS.APPRAISAL,
  OS.BUYING,
  OS.DOCUMENT_PREPARATION,
  OS.PAYMENT_TRANSFER,
  OS.TRANSPORTATION,
  OS.COMPLETED,
  OS.CANCELLED,
  OS.REFUSED_BY_INSPECTOR,
  OS.REFUSED_BY_SELLER,
  OS.REFUSED_BUYOUT,
]

export interface OrderFilterState {
  status: OrderStatus[]
  search: string
  dateFrom: string
  dateTo: string
  /** Тип заказчика: '' (любой) | 'company' (B2B) | 'individual' (Физлицо). */
  customerKind: '' | 'company' | 'individual'
}

const CUSTOMER_KIND_OPTIONS: { value: 'company' | 'individual'; label: string }[] = [
  { value: 'company', label: 'B2B' },
  { value: 'individual', label: 'Физлицо' },
]

export function OrderFilters({
  value,
  onChange,
  onApply,
  onReset,
}: {
  value: OrderFilterState
  onChange: (v: OrderFilterState) => void
  onApply: () => void
  onReset: () => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const activeCount =
    value.status.length +
    (value.search ? 1 : 0) +
    (value.dateFrom || value.dateTo ? 1 : 0) +
    (value.customerKind ? 1 : 0)

  function toggleStatus(s: OrderStatus) {
    const set = new Set(value.status)
    if (set.has(s)) set.delete(s)
    else set.add(s)
    onChange({ ...value, status: [...set] })
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          placeholder={t('orders.filters.searchPlaceholder')}
          value={value.search}
          onChange={(e) => onChange({ ...value, search: e.target.value })}
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpen((o) => !o)}
        >
          {t('common.filters')}
          {activeCount ? (
            <span className="rounded-[var(--stryzh-radius-pill)] bg-[color:var(--stryzh-color-accent-orange)] px-2 py-0.5 font-[family-name:var(--stryzh-font-family-mono)] text-[length:var(--stryzh-font-size-xs)] font-bold text-[color:var(--stryzh-color-text-on-orange-badge)]">
              {activeCount}
            </span>
          ) : null}
        </Button>
      </div>
      {open ? (
        <div className="space-y-3 rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] px-3 py-2.5">
          <div className="flex items-start gap-3">
            <div className="pt-1 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
              {t('orders.filters.status')}
            </div>
            <div className="flex flex-1 flex-wrap gap-2">
              {ALL_STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={clsx(
                    'stryzh-filter-chip',
                    value.status.includes(s) && 'stryzh-filter-chip--selected',
                  )}
                  onClick={() => toggleStatus(s)}
                >
                  {statusLabelRu(s)}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="pt-1 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
              {t('orders.filters.type')}
            </div>
            <div className="flex flex-1 flex-wrap gap-2">
              {CUSTOMER_KIND_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={clsx(
                    'stryzh-filter-chip',
                    value.customerKind === opt.value &&
                      'stryzh-filter-chip--selected',
                  )}
                  onClick={() =>
                    onChange({
                      ...value,
                      customerKind:
                        value.customerKind === opt.value ? '' : opt.value,
                    })
                  }
                >
                  {opt.value === 'individual' ? t('orderKind.individual') : opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
              {t('orders.filters.period')}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="date"
                className="h-9 rounded-[var(--stryzh-radius-lg)] border border-[var(--stryzh-color-border-default)] bg-transparent px-2 text-sm text-[var(--stryzh-color-text-primary)] outline-none transition-colors focus:border-[var(--stryzh-color-accent-orange)]"
                aria-label={t('orders.filters.dateFrom')}
                value={value.dateFrom}
                onChange={(e) => onChange({ ...value, dateFrom: e.target.value })}
              />
              <span className="text-[var(--stryzh-color-text-secondary)]">-</span>
              <input
                type="date"
                className="h-9 rounded-[var(--stryzh-radius-lg)] border border-[var(--stryzh-color-border-default)] bg-transparent px-2 text-sm text-[var(--stryzh-color-text-primary)] outline-none transition-colors focus:border-[var(--stryzh-color-accent-orange)]"
                aria-label={t('orders.filters.dateTo')}
                value={value.dateTo}
                onChange={(e) => onChange({ ...value, dateTo: e.target.value })}
              />
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Button type="button" variant="ghost" onClick={onReset}>
                {t('common.reset')}
              </Button>
              <Button type="button" variant="primary" onClick={onApply}>
                {t('common.apply')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
